import React, { ChangeEvent, JSX, useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import Keys from '../Translations/generated/product-ibe.de.json.keys';
import fallback from '../Translations/generated/product-ibe.de.json';
import { Alert, Button, Input } from 'reactstrap';
import { LoggerFactory } from '@ibe/services';
import { useConfig, useTranslation } from '@ibe/components';
import { ValidPromotionCode, validPromotionCodeStorage } from '../Pages/Util/utils';
import useSearchApi from '../Util/useSearchApi';
import { Api, ApiPromotionDataRequestFromJSON } from '../../api';
import { PackageCode } from '../Pages/Products/PackagesList';
import { useMount } from 'react-use';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';

const logger = LoggerFactory.get('PromotionCode');

export type PromotionCodeType = ValidPromotionCode | 'NONE' | 'ERROR' | undefined;
export type PromotionCodeData = {
  promotionCodeData: PromotionCodeType;
  initialPromotionCodeChecked: boolean;
};
type PromotionError =
  | 'DEFAULT'
  | 'USE_PREV_CODE_AGAIN'
  | 'OLD_CODE_IS_BETTER'
  | 'NEW_CODE_IS_OLD_CODE'
  | undefined;

// extend window object with the CMS function to update the current price by the given promo discount
type WindowWithCMSFunction = Window & {
  productDetailsUpdatePromoCode: (promoDiscount: number) => void;
};

declare const window: WindowWithCMSFunction;

export const isValidPromotionCode = (code: PromotionCodeType): code is ValidPromotionCode => {
  return typeof code === 'object' && code.hasOwnProperty('code') && !!code.code;
};

export const validatePromotionCode = async (
  searchApi: Api,
  packageCodes: Array<string>,
  code?: string,
  basePrice?: string,
  startDate?: string,
  endDate?: string,
  sign?: string
): Promise<ValidPromotionCode> => {
  const result = await searchApi.addPromotionCode(
    ApiPromotionDataRequestFromJSON({
      packageCodes: packageCodes,
      promotionCandidate: code,
      basePrice: basePrice ?? undefined,
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined
    })
  );
  const resPromotionCode =
    result?.item?.percentagePromotionCode ?? result?.item?.absolutePromotionCode ?? '';

  return {
    code: resPromotionCode,
    percentageValue: result?.item?.percentage,
    absoluteValue: result?.item?.absoluteDiscount,
    preSign: result?.item?.absolutePromotionCode ? sign + ' ' : '',
    sign: result?.item?.percentagePromotionCode ? ' %' : '',
    productCode: result?.item?.code
  };
};

function formatDiscount(price?: number): string {
  if (!price && price !== 0) return '0';

  const rounded = Math.round(price * 100) / 100;
  const [whole, decimal] = rounded.toFixed(2).split('.');

  return decimal === '00' ? whole : `${whole},${decimal}`;
}

interface PromotionCodeProps {
  onSubmit?: (code: PromotionCodeType) => void;
  setPromotionCode?: (promotionCode: PromotionCodeType) => void;
  disabled?: boolean;
  packageCodes: Array<PackageCode>;
  seasonPackageCode?: string;
  seasonBestPriceDate?: string;
  startDate?: string;
  endDate?: string;
  smallMode?: boolean;
  promotionCode: PromotionCodeType;
  skipCodeComparison?: boolean;
  checkForCodeInStorage?: boolean;
}

const promoCodeIcon = (
  <svg
    role="presentation"
    focusable="false"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24.749 24.874"
  >
    <g id="discount-icon" transform="translate(-6412.747 -195.859)">
      <path
        id="Differenzmenge_2"
        data-name="Differenzmenge 2"
        d="M10.607,24.042h0L0,13.435,13.435,0,24.042,10.607,10.607,24.041Zm.63-9.33h0a3.684,3.684,0,0,0,2.069.714,2.735,2.735,0,0,0,1.96-.842,2.209,2.209,0,0,0,.526-2.325l-.891.161a1.364,1.364,0,0,1-.356,1.357,1.342,1.342,0,0,1-.979.411,2.514,2.514,0,0,1-1.336-.47l1.629-1.629-.577-.577-1.706,1.705c-.1-.085-.2-.179-.3-.28a2.741,2.741,0,0,1-.213-.229l2.045-2.046-.577-.577-1.985,1.985c-.632-.944-.659-1.785-.077-2.367a1.326,1.326,0,0,1,.994-.411,2.126,2.126,0,0,1,.219.012l.068-.967a2.206,2.206,0,0,0-.473-.052,2.27,2.27,0,0,0-1.624.705,3.073,3.073,0,0,0-.085,4.056l-.509.594.535.535.483-.484a3.4,3.4,0,0,0,.247.263,3.717,3.717,0,0,0,.271.255l-.449.518.535.535.551-.55Z"
        transform="translate(6412.747 196.297)"
        fill="#004f9e"
      />
      <ellipse
        id="Ellipse_100"
        data-name="Ellipse 100"
        cx="5.817"
        cy="3.996"
        rx="5.817"
        ry="3.996"
        transform="translate(6429.269 195.859) rotate(45)"
        fill="#004f9e"
      />
      <ellipse
        id="Ellipse_101"
        data-name="Ellipse 101"
        cx="5.556"
        cy="3.996"
        rx="5.556"
        ry="3.996"
        transform="translate(6418.449 207.223) rotate(45)"
        fill="#004f9e"
      />
    </g>
  </svg>
);

const PromotionCodeWrapper = (props: PromotionCodeProps) => {
  const { smallMode } = props;
  if (smallMode) return <PromotionCodeSmallMode {...props} />;
  return <PromotionCode {...props} />;
};

const PromotionCode = (props: PromotionCodeProps): JSX.Element => {
  const {
    code,
    handleClearClick,
    handleKeyDownSubmit,
    promotionError,
    promotionSuccess,
    submit,
    togglePromotionError,
    inputRef,
    t,
    setCode
  } = usePromotionCode(props);

  const { promotionCode, disabled } = props;

  return (
    <section role="region" aria-label={t(Keys.promotionCodeEntry)}>
      <div className={`prom-code__container__outer`}>
        <div className={`prom-code__container`}>
          <div className="prom-code__left">
            <h3>{t(Keys.promotionCodeHeadline)}</h3>
            {!isValidPromotionCode(promotionCode) && !promotionError ? (
              <p>{t(Keys.promotionCodeText)}</p>
            ) : (
              <></>
            )}

            <div className="prom-code__input">
              <div className="prom-code__input__inner">
                <Input
                  className={'input--blue-outline'}
                  innerRef={inputRef}
                  value={code}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.code === 'Enter') handleKeyDownSubmit(e);
                  }}
                  disabled={disabled}
                  aria-label={t(Keys.promotionCodeHeadline)}
                />
                <FontAwesomeIcon icon={faTimesCircle} onClick={handleClearClick} />
              </div>
              <Button color="primary" onClick={submit} disabled={code === ''}>
                {t(Keys.submit)}
              </Button>
            </div>
            {isValidPromotionCode(promotionCode) ? (
              <Alert className="mt-4" color="success" fade isOpen={promotionSuccess}>
                {t(Keys.promotionCodeSuccess, {
                  code: promotionCode.code,
                  discount: formatDiscount(
                    promotionCode.percentageValue ?? promotionCode.absoluteValue ?? 0
                  ),
                  preSign: promotionCode.preSign,
                  sign: promotionCode.sign
                })}
              </Alert>
            ) : (
              <></>
            )}
            <Alert
              color="warning"
              className="mt-4"
              fade
              isOpen={!!promotionError}
              toggle={togglePromotionError}
            >
              {t(Keys.promotionError)}
            </Alert>
          </div>
        </div>
      </div>
    </section>
  );
};

const PromotionCodeSmallMode = (props: PromotionCodeProps) => {
  const {
    code,
    handleClearClick,
    handleKeyDownSubmit,
    promotionError,
    promotionSuccess,
    submit,
    togglePromotionError,
    inputRef,
    t,
    isEditing,
    toggleEditing,
    getSavedOrOldCode,
    setCode
  } = usePromotionCode({ ...props, checkForCodeInStorage: true });
  const { promotionCode, disabled } = props;

  const header = (
    <div className={`prom-code__container prom-code__container--small align-items-center`}>
      <div className="prom-code__icon">{promoCodeIcon}</div>
      <h3>{t(Keys.promotionCode)}</h3>
    </div>
  );
  const oldCode = getSavedOrOldCode();
  if (!isEditing) {
    return (
      <section role="region" aria-label={t(Keys.promotionCodeEntry)}>
        <div className={`mt-3 mb-4`}>
          {header}
          <div className="prom-code__not_editing">
            <div>
              {isValidPromotionCode(promotionCode) && (
                <div className="mt-2">
                  {t(Keys.promotionCodeSuccess, {
                    code: promotionCode.code,
                    discount: formatDiscount(
                      promotionCode.percentageValue ?? promotionCode.absoluteValue ?? 0
                    ),
                    preSign: promotionCode.absoluteValue ? `${promotionCode.sign} ` : undefined,
                    sign: promotionCode.percentageValue ? `${promotionCode.sign} ` : undefined
                  })}
                </div>
              )}
            </div>
            <div>
              <Button color="secondary" onClick={() => toggleEditing()}>
                <FontAwesomeIcon icon={faPencilAlt} />
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section role="region" aria-label={t(Keys.promotionCodeEntry)}>
      <div className={`prom-code__container__outer mt-3`}>
        {header}
        <div>
          {!isValidPromotionCode(promotionCode) && !promotionError && (
            <div className="mt-2">{t(Keys.promotionCodeText)}</div>
          )}
          {isValidPromotionCode(promotionCode) && (
            <div className="mt-2">
              {t(Keys.promotionCodeSuccess, {
                code: promotionCode.code,
                discount: formatDiscount(
                  promotionCode.percentageValue ?? promotionCode.absoluteValue ?? 0
                ),
                preSign: promotionCode.absoluteValue ? `${promotionCode.sign} ` : undefined,
                sign: promotionCode.percentageValue ? `${promotionCode.sign} ` : undefined
              })}
            </div>
          )}
        </div>

        <div
          className={`${
            isValidPromotionCode(promotionCode) ? 'mt-3' : ''
          } prom-code__container prom-code__container--small`}
        >
          <div>
            <Alert
              className="prom-code__alert mt-2"
              color="warning"
              fade
              isOpen={!!promotionError}
              toggle={togglePromotionError}
            >
              {promotionError === 'OLD_CODE_IS_BETTER'
                ? t(Keys.promotionErrorOldIsBetter, { oldCode })
                : promotionError === 'USE_PREV_CODE_AGAIN'
                ? t(Keys.promotionErrorAdditional, { oldCode })
                : promotionError === 'NEW_CODE_IS_OLD_CODE'
                ? t(Keys.promotionErrorNewIsOld, { oldCode })
                : t(Keys.promotionError)}
            </Alert>
            <div className="prom-code__input">
              <div className="prom-code__input__inner">
                <Input
                  innerRef={inputRef}
                  value={code}
                  placeholder={t(Keys.addHere)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.code === 'Enter') handleKeyDownSubmit(e);
                  }}
                  disabled={disabled}
                />
                {code ? <FontAwesomeIcon icon={faTimesCircle} onClick={handleClearClick} /> : null}
              </div>
              <Button color="secondary" onClick={submit} disabled={code === ''}>
                {t(Keys.submit)}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const usePromotionCode = ({
  onSubmit,
  promotionCode,
  packageCodes,
  skipCodeComparison,
  checkForCodeInStorage,
  setPromotionCode,
  startDate,
  endDate,
  seasonBestPriceDate,
  seasonPackageCode
}: PromotionCodeProps) => {
  const { t } = useTranslation('product-ibe', fallback);
  const searchApi = useSearchApi();
  const [code, setCode] = useState<string>('');
  const [promotionError, setPromotionError] = useState<PromotionError>(undefined);
  const [promotionSuccess, setPromotionSuccess] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);
  const initialMount = useRef<boolean>(true);
  const config = useConfig();
  const sign = config?.currency
    ? new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: config.currency,
        currencyDisplay: 'symbol'
      })
        .formatToParts(0)
        .find(p => p.type === 'currency')?.value ?? config.currency
    : '€';
  useMount(() => {
    if (checkForCodeInStorage) {
      const data = validPromotionCodeStorage.get();
      if (!!data?.code) {
        setCode(data.code);
        setIsEditing(false);
        if (!!setPromotionCode) {
          setPromotionCode(data);
        }
      }
    }
    if (isValidPromotionCode(promotionCode)) {
      setPromotionSuccess(false);
      setPromotionError(undefined);
      setIsEditing(false);
    }
    initialMount.current = false;
  });

  const setError = (promotionErrorString?: PromotionError, keepCode?: boolean): void => {
    setPromotionSuccess(false);
    const data = validPromotionCodeStorage.get();
    if (!keepCode) setCode(!!data?.code ? data.code : '');
    setPromotionError(promotionErrorString || (!!data?.code ? 'USE_PREV_CODE_AGAIN' : 'DEFAULT'));
  };
  const getSavedOrOldCode = () => {
    return validPromotionCodeStorage.get()?.code;
  };

  const togglePromotionError = useCallback(() => {
    setPromotionError(undefined);
    const data = validPromotionCodeStorage.get();
    if (!!data?.code) {
      setIsEditing(false);
      if (onSubmit) {
        onSubmit(data);
      }
    }
  }, []);

  const handleKeyDownSubmit = (e: React.KeyboardEvent) => {
    e.preventDefault();
    submit().then();
  };

  const toggleEditing = () => {
    setIsEditing(!isEditing);
    setPromotionSuccess(true);
  };

  interface PromotionData {
    code: string;
    percentageValue?: number;
    absoluteValue?: number;
    manuallyAdded?: boolean;
  }

  interface ValidPromotionCodeStorage {
    get: () => PromotionData | undefined;
    set: (data: PromotionData) => void;
  }

  const formatDate = (date: string | undefined): string | undefined => {
    return date ? date.split('T')[0] : undefined;
  };

  const isNewCodeSameAsOld = (newCode: string, oldCode?: string): boolean => {
    return newCode === oldCode;
  };

  const isOldCodeBetter = (oldData: PromotionData | undefined, newData: PromotionData): boolean => {
    if (!oldData) return false;

    const hasOldPercentage = !!oldData.percentageValue;
    const hasNewPercentage = !!newData.percentageValue;
    const hasOldAbsolute = !!oldData.absoluteValue;
    const hasNewAbsolute = !!newData.absoluteValue;

    // Old percentage is better than new percentage
    if (
      hasOldPercentage &&
      hasNewPercentage &&
      oldData.percentageValue &&
      newData.percentageValue
    ) {
      return oldData.percentageValue > newData.percentageValue * 100;
    }

    // Old absolute is better than new absolute
    if (hasOldAbsolute && hasNewAbsolute && oldData.absoluteValue && newData.absoluteValue) {
      return oldData.absoluteValue > newData.absoluteValue;
    }

    // Old has percentage but new doesn't
    if (hasOldPercentage && !hasNewPercentage) {
      return true;
    }

    return false;
  };

  const handleCodeValidation = (
    promotionData: PromotionData,
    oldData: PromotionData | undefined,
    shouldSkipCodeComparison: undefined | boolean,
    errorSetter: (promotionErrorString?: PromotionError, keepCode?: boolean) => void
  ): boolean => {
    if (!shouldSkipCodeComparison && isNewCodeSameAsOld(promotionData.code, oldData?.code)) {
      errorSetter('NEW_CODE_IS_OLD_CODE');
      return false;
    }

    if (!shouldSkipCodeComparison && isOldCodeBetter(oldData, promotionData)) {
      errorSetter('OLD_CODE_IS_BETTER');
      return false;
    }

    return true;
  };

  const updateSeasonData = async (
    apiClient: Api,
    packageCode: string | undefined,
    promoCode: string,
    bestPriceDate: string | undefined
  ): Promise<void> => {
    if (!window?.productDetailsUpdatePromoCode || !packageCode) {
      return;
    }

    const seasonData = await validatePromotionCode(
      apiClient,
      [packageCode],
      promoCode,
      undefined,
      bestPriceDate,
      bestPriceDate
    );

    if (seasonData.percentageValue || seasonData.absoluteValue) {
      const discount = seasonData.percentageValue
        ? seasonData.percentageValue / 100
        : (seasonData.absoluteValue as number);

      window.productDetailsUpdatePromoCode(discount);
    }
  };

  interface HandleSuccessfulValidationParams {
    promotionData: PromotionData;
    storage: ValidPromotionCodeStorage;
    successSetter: (success: boolean) => void;
    apiClient: Api;
    packageCode?: string;
    promoCode: string;
    bestPriceDate?: string;
    submitCallback?: (data: PromotionData) => void;
  }

  const handleSuccessfulValidation = async ({
    promotionData,
    storage,
    successSetter,
    apiClient,
    packageCode,
    promoCode,
    bestPriceDate,
    submitCallback
  }: HandleSuccessfulValidationParams): Promise<void> => {
    storage.set({ ...promotionData, manuallyAdded: true });
    successSetter(true);

    await updateSeasonData(apiClient, packageCode, promoCode, bestPriceDate);

    if (submitCallback) {
      submitCallback({ ...promotionData });
    }
  };

  const submit = async (): Promise<void> => {
    if (code === '') {
      return;
    }

    setPromotionError(undefined);

    try {
      const data = await validatePromotionCode(
        searchApi,
        packageCodes.map(item => item.code),
        code,
        undefined,
        formatDate(startDate),
        formatDate(endDate),
        sign
      );

      if (!data.code) {
        setPromotionSuccess(false);
        setError();
        return;
      }

      const oldData = validPromotionCodeStorage.get();
      const isValidCode = handleCodeValidation(data, oldData, skipCodeComparison, setError);

      if (isValidCode) {
        await handleSuccessfulValidation({
          promotionData: data,
          storage: validPromotionCodeStorage,
          successSetter: setPromotionSuccess,
          apiClient: searchApi,
          packageCode: seasonPackageCode,
          promoCode: code,
          bestPriceDate: seasonBestPriceDate,
          submitCallback: onSubmit
        });
      }
    } catch (err) {
      logger.error(err);
      setError();
    }
  };

  const handleClearClick = (): void => {
    setCode('');
    if (!!inputRef.current) {
      inputRef.current.focus();
    }
  };
  return {
    handleClearClick,
    handleKeyDownSubmit,
    submit,
    togglePromotionError,
    setError,
    code,
    promotionError,
    promotionSuccess,
    inputRef,
    t,
    setCode,
    isEditing,
    toggleEditing,
    getSavedOrOldCode
  };
};

export default PromotionCodeWrapper;
